/* MUI */
import { AvatarGroup, Box } from '@mui/material';
import { FontSizes } from 'application/Shared/Constants';

/* React */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LightTooltip } from '../Tooltips';

/* Components */
import { TagChip } from './TagChip';

/**
 * Filters and sorts tag links based on specific criteria
 * @param {Array} tagLinks - Array of tag link objects to process
 * @returns {Array} Filtered and sorted array of tag links that:
 *  - Have applyTag property set to true
 *  - Have a valid globalTag name
 *  - Are sorted alphabetically by globalTag name
 */
const applyAndSortTagLinks = (tagLinks) => {
    const appliedTagLinksWithName = tagLinks?.filter(
        (tl) => tl?.applyTag && Boolean(tl?.globalTag?.name)
    );
    if (!appliedTagLinksWithName?.length) {
        return [];
    }
    return appliedTagLinksWithName.sort((a, b) =>
        a.globalTag?.name.localeCompare(b.globalTag?.name)
    );
};

/**
 * Displays a list of tag links as chips
 * @param {Array} tagLinks a list of tag links to be displayed
 * @returns {JSX.Element}
 */
export const TagLinksList = ({ tagLinks = [] }) => {
    const { t } = useTranslation();

    const sortedTagLinks = applyAndSortTagLinks(tagLinks);

    if (!sortedTagLinks?.length) {
        return <Box>{t('Tags.noTagsFound')}</Box>;
    }

    return sortedTagLinks.map((tl) => {
        const { name } = tl.globalTag;

        const color = tl.color || tl?.globalTag?.defaultColor || 'tag.option1';

        return (
            <TagChip
                data-testid={`tag-chip-${tl.id}`}
                key={tl.id}
                name={name}
                color={color}
                additionalSxStyles={{
                    marginBottom: '4px'
                }}
            />
        );
    });
};

/**
 * Renders two tags, a "+n" bubble and a tooltip
 * @param {Array} tagLinks a list of tag links to be displayed
 * @returns {JSX.Element|Array} Avatar group with tags or empty array if no tags
 */

export const TagsBubbles = ({ tagLinks = [] }) => {
    const { t } = useTranslation();

    const sortedTagLinks = applyAndSortTagLinks(tagLinks);

    if (!sortedTagLinks?.length) {
        return <Box>{t('Tags.noTagsFound')}</Box>;
    }

    const showTooltip = sortedTagLinks?.length > 3;

    return (
        <LightTooltip
            title={
                <Box
                    maxHeight="160px"
                    display="flex"
                    p={2}
                    flexDirection="column"
                    alignItems="flex-start"
                    sx={{
                        fontSize: FontSizes[0],
                        fontWeight: 600,
                        marginBottom: '4px',
                        marginTop: '4px',
                        overflow: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    <TagLinksList tagLinks={tagLinks} />
                </Box>
            }
            placement="right"
            arrow
            disableHoverListener={!showTooltip}
        >
            <AvatarGroup
                data-testid="tagsAvatarGroup"
                max={3}
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap-reverse',
                    justifyContent: 'left'
                }}
            >
                {sortedTagLinks.map((tl) => {
                    const { name } = tl.globalTag;
                    const color =
                        tl.color ||
                        tl?.globalTag?.defaultColor ||
                        'tag.option1';

                    return (
                        <TagChip
                            data-testid={`tag-chip-${tl.id}`}
                            key={tl.id}
                            name={name}
                            color={color}
                            additionalSxStyles={{
                                marginBottom: '4px'
                            }}
                        />
                    );
                })}
            </AvatarGroup>
        </LightTooltip>
    );
};
